import React, { type CSSProperties } from 'react';
import type { ActionDefinition, ActionIntent } from '@/theme/actions';
import { Button, IconButton, Spinner, Tooltip } from '@/components/primitives';
import {
  actionIconButton,
  barIconButton,
  compactIconButton,
  overlayIconButton,
  outlinedIconButton,
  confirmIconButton,
  transparentIconButton,
  subheaderIconButton,
  ctaIconButtonClass,
  labeledButton,
  subheaderButtonClass,
  primaryButton,
  secondaryButtonClass,
  outlinedDialogButtonClass,
  consumeButton,
  inlineButtonClass,
  errorButtonClass,
  subtleActionButtonClass,
} from '@/components/primitives/Button/button.css';

export type ActionButtonMode =
  | 'icon'
  | 'icon-bar'
  | 'icon-compact'
  | 'icon-overlay'
  | 'icon-outlined'
  | 'icon-confirm'
  | 'icon-cta'
  | 'icon-transparent'
  | 'icon-subheader'
  | 'labeled'
  | 'cta'
  | 'dialog-primary'
  | 'dialog-secondary'
  | 'dialog-outlined'
  | 'consume'
  | 'inline'
  | 'error'
  | 'subtle';

export interface ActionButtonProps {
  /** Action definition from Actions registry */
  action: ActionDefinition;
  /** Click handler (optional for type="submit" buttons inside forms) */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Override tooltip (for context-specific messages like "Select items first") */
  tooltipOverride?: string;
  /** Override intent (when context requires different styling than default) */
  intentOverride?: ActionIntent;
  /** Button size for icon modes */
  size?: 'small' | 'medium' | 'large';
  /** Additional inline styles */
  style?: CSSProperties;
  /**
   * @deprecated Use `style` instead. MUI sx shorthand will not be resolved.
   * Kept for backward compatibility during migration.
   */
  sx?: React.CSSProperties;
  /** Rendering mode. Defaults to 'icon'. */
  mode?: ActionButtonMode;
  /** Button label content (for non-icon modes) */
  label?: React.ReactNode;
  /** Whether to show the action's icon. Defaults vary by mode. */
  showIcon?: boolean;
  /** Custom icon element to replace the action's icon (e.g., brand images for OAuth) */
  iconElement?: React.ReactNode;
  /** End icon (e.g., dropdown arrow) */
  endIcon?: React.ReactNode;
  /** Show loading spinner */
  loading?: boolean;
  /** Label to show while loading (falls back to label) */
  loadingLabel?: string;
  /** HTML button type (for form submission) */
  type?: 'button' | 'submit';
  /** Whether button spans full width of container */
  fullWidth?: boolean;
  /** Override the action's testId (when consumer needs a specific testId) */
  testIdOverride?: string;
  /** URL for link-style buttons (renders as anchor element) */
  href?: string;
  /** Link target (e.g., '_blank' for new tab). Requires href. */
  target?: string;
  /** Anchor rel attribute. Defaults to "noopener noreferrer" when target="_blank" to prevent tabnabbing. */
  rel?: string;
  /** Download attribute for link buttons. Requires href. */
  download?: boolean | string;
  /** Element type override for polymorphism (e.g., Link for router links) */
  component?: React.ElementType;
  /** Additional CSS class name */
  className?: string;
}

const ICON_MODES = new Set<ActionButtonMode>([
  'icon',
  'icon-bar',
  'icon-compact',
  'icon-overlay',
  'icon-outlined',
  'icon-confirm',
  'icon-cta',
  'icon-transparent',
  'icon-subheader',
]);

const DEFAULT_SHOW_ICON: Record<ActionButtonMode, boolean> = {
  'icon': true,
  'icon-bar': true,
  'icon-compact': true,
  'icon-overlay': true,
  'icon-outlined': true,
  'icon-confirm': true,
  'icon-cta': true,
  'icon-transparent': true,
  'icon-subheader': true,
  'labeled': true,
  'cta': true,
  'dialog-primary': false,
  'dialog-secondary': false,
  'dialog-outlined': false,
  'consume': false,
  'inline': false,
  'error': true,
  'subtle': true,
};

function getIconModeClass(mode: ActionButtonMode, intent: ActionIntent): string {
  switch (mode) {
    case 'icon-bar':         return barIconButton[intent];
    case 'icon-compact':     return compactIconButton[intent];
    case 'icon-overlay':     return overlayIconButton[intent];
    case 'icon-outlined':    return outlinedIconButton[intent];
    case 'icon-confirm':     return confirmIconButton[intent];
    case 'icon-cta':         return ctaIconButtonClass;
    case 'icon-transparent': return transparentIconButton[intent];
    case 'icon-subheader':   return subheaderIconButton[intent];
    default:                 return actionIconButton[intent];
  }
}

function getButtonModeClass(mode: ActionButtonMode, intent: ActionIntent): string {
  switch (mode) {
    case 'labeled':          return labeledButton[intent];
    case 'cta':              return subheaderButtonClass;
    case 'dialog-primary':   return intent === 'destructive' ? primaryButton.destructive : primaryButton.confirm;
    case 'dialog-secondary': return secondaryButtonClass;
    case 'dialog-outlined':  return outlinedDialogButtonClass;
    case 'consume':          return consumeButton[intent];
    case 'inline':           return inlineButtonClass;
    case 'error':            return errorButtonClass;
    case 'subtle':           return subtleActionButtonClass;
    default:                 return '';
  }
}

/**
 * ActionButton
 *
 * Standard action button that uses the centralized Actions registry.
 * Ensures consistent icons, tooltips, test IDs, and intent-based styling.
 *
 * The `mode` prop controls rendering: icon-only, labeled, CTA, dialog, etc.
 * The action's purpose determines intent (color), which can be overridden.
 *
 * forwardRef + `...rest` passthrough so Radix `asChild` triggers
 * (Popover/Tooltip/DropdownMenu) can anchor to the button and inject their
 * event handlers/aria attributes. Without this, e.g. the responsive "Filter"
 * overflow popover trigger renders with no anchor and clicking does nothing.
 */
export const ActionButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ActionButtonProps
>(function ActionButton({
  action,
  onClick,
  disabled = false,
  tooltipOverride,
  intentOverride,
  size = 'small',
  style,
  // Deprecated: intentionally destructured to drop it (never forwarded to DOM).
  sx: _sx,
  mode = 'icon',
  label,
  showIcon,
  iconElement,
  endIcon,
  loading = false,
  loadingLabel,
  type,
  fullWidth = false,
  testIdOverride,
  href,
  target,
  rel,
  download,
  component,
  className,
  ...rest
}, ref) {
  const Icon = action.icon;
  const intent = intentOverride ?? action.intent;
  const isDisabled = disabled || loading;
  const shouldShowIcon = showIcon ?? DEFAULT_SHOW_ICON[mode];
  const testId = testIdOverride ?? action.testId;
  const iconFontSize = size === 'small' ? 18 : size === 'large' ? 32 : 24;
  // Tabnabbing guard: default rel when opening in a new tab.
  const resolvedRel = rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  // Icon modes: IconButton with tooltip
  if (ICON_MODES.has(mode)) {
    const tooltip = tooltipOverride ?? action.tooltip;
    const modeClass = [getIconModeClass(mode, intent), className].filter(Boolean).join(' ');

    return (
      <Tooltip content={tooltip}>
        <span>
          <IconButton
            {...rest}
            ref={ref as React.Ref<HTMLButtonElement>}
            onClick={onClick as React.MouseEventHandler}
            disabled={isDisabled}
            data-testid={testId}
            aria-label={tooltip}
            as={href ? (component ?? 'a') : undefined}
            href={href}
            target={target}
            rel={resolvedRel}
            download={download}
            className={modeClass}
            style={style}
          >
            {loading
              ? <Spinner size={18} color="inherit" />
              : (iconElement ?? <Icon style={{ fontSize: iconFontSize }} />)
            }
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Button modes
  const modeClass = [getButtonModeClass(mode, intent), className].filter(Boolean).join(' ');
  const buttonLabel = loading ? (loadingLabel ?? label) : label;

  const startIcon = loading
    ? <Spinner size={16} color="inherit" />
    : shouldShowIcon
      ? (iconElement ?? <Icon style={{ fontSize: 16 }} />)
      : undefined;

  const tooltip = tooltipOverride;

  const button = (
    <Button
      {...rest}
      ref={ref as React.Ref<HTMLButtonElement>}
      onClick={onClick as React.MouseEventHandler}
      disabled={isDisabled}
      data-testid={testId}
      startIcon={startIcon}
      endIcon={endIcon}
      type={type}
      fullWidth={fullWidth}
      as={href ? (component ?? 'a') : undefined}
      href={href}
      target={target}
      rel={resolvedRel}
      download={download}
      className={modeClass}
      style={style}
    >
      {buttonLabel}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
});

ActionButton.displayName = 'ActionButton';
